import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ChevronDown, 
  ChevronUp, 
  Compass,
  MapPin, 
  Clock, 
  Users,
  Search,
  ArrowRight,
  User,
  AlertTriangle,
  Briefcase,
  GitBranch,
  FileQuestion,
  Loader2,
  Dna,
  BookmarkPlus
} from 'lucide-react';

import { treeHref, personHref } from '../utils/prettyArchiveLinks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Confidence badge styling - investigative tone
const ConfidenceBadge = ({ confidence }) => {
  const styles = {
    verified: 'bg-emerald-900/30 text-emerald-400 border-emerald-800/50',
    likely: 'bg-amber-900/20 text-amber-400/90 border-amber-800/40',
    possible: 'bg-secondary text-muted-foreground border-border'
  };
  
  const labels = {
    verified: 'Verified',
    likely: 'Likely',
    possible: 'Possible'
  };
  
  return (
    <span className={`text-[9px] px-2 py-0.5 rounded-full border uppercase tracking-[0.1em] font-medium ${styles[confidence] || styles.possible}`}>
      {labels[confidence] || 'Unknown'}
    </span>
  );
};

// Icon for lead type
const LeadIcon = ({ type }) => {
  const iconClass = "w-4 h-4 text-amber-500/80";
  
  switch (type) {
    case 'generation_gap':
      return <AlertTriangle className={iconClass} />;
    case 'migration':
      return <MapPin className={iconClass} />;
    case 'occupation':
      return <Briefcase className={iconClass} />;
    case 'surname_cluster':
      return <Users className={iconClass} />;
    case 'cousin_match':
      return <Dna className={iconClass} />;
    case 'undocumented':
      return <FileQuestion className={iconClass} />;
    default:
      return <Compass className={iconClass} />;
  }
};

// Single Lead Card
const LeadCard = ({ lead, treeForNav, treeName, onInvestigate, onSaveToJournal, isSavingToJournal }) => {
  const navigate = useNavigate();
  /** Canonical tree id may be missing briefly while parent loads; avoid bad navigations. */
  const treeBase = treeForNav?.id ? treeHref(treeForNav) : null;

  const handleInvestigate = () => {
    const { action_type, action_params } = lead;
    
    if (action_type === 'gene_engine') {
      // Navigate to Gene Engine with pre-filled search
      const params = new URLSearchParams();
      if (action_params.surname) params.set('name', action_params.surname);
      if (action_params.yearStart) params.set('yearStart', action_params.yearStart);
      if (action_params.yearEnd) params.set('yearEnd', action_params.yearEnd);
      if (action_params.location) params.set('location', action_params.location);
      if (action_params.includeClusters) params.set('clusters', 'true');
      
      navigate(`/gene-engine?${params.toString()}`);
    } else if (action_type === 'profile' && action_params.person_id) {
      // Navigate to person profile
      navigate(personHref({ id: action_params.person_id }));
    } else if (action_type === 'investigation') {
      // Could open investigation mode - for now, go to tree with search
      if (treeBase) {
        if (lead.related_people?.[0]?.name) {
          navigate(`${treeBase}?search=${encodeURIComponent(lead.related_people[0].name)}`);
        } else {
          navigate(treeBase);
        }
      }
    }
    
    if (onInvestigate) {
      onInvestigate(lead);
    }
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card className="bg-card border-secondary hover:border-amber-900/40 transition-all duration-300">
        <CardContent className="p-4">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex items-start gap-2.5">
              <div className="mt-0.5 p-1.5 rounded-lg bg-amber-900/20 border border-amber-800/30">
                <LeadIcon type={lead.lead_type} />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-medium text-foreground/90 leading-tight">
                  {lead.title}
                </h4>
              </div>
            </div>
            <ConfidenceBadge confidence={lead.confidence} />
          </div>
          
          {/* Description */}
          <p className="text-xs text-muted-foreground/80 leading-relaxed mb-3 pl-9">
            {lead.description}
          </p>
          
          {/* Related People (if any) */}
          {lead.related_people && lead.related_people.length > 0 && (
            <div className="pl-9 mb-3">
              <div className="flex flex-wrap gap-1">
                {lead.related_people.slice(0, 3).map((person, idx) => (
                  <span 
                    key={idx}
                    className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-secondary text-foreground border border-border"
                  >
                    <User className="w-2.5 h-2.5" />
                    {person.name}
                  </span>
                ))}
                {lead.related_people.length > 3 && (
                  <span className="text-[10px] text-muted-foreground px-1">
                    +{lead.related_people.length - 3} more
                  </span>
                )}
              </div>
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="pl-9 flex flex-wrap gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleInvestigate}
              className="h-7 px-3 text-xs text-amber-400/90 hover:text-amber-300 hover:bg-amber-900/20 border border-amber-800/30 rounded-full"
              data-testid={`investigate-lead-${lead.id}`}
            >
              <Search className="w-3 h-3 mr-1.5" />
              Investigate
              <ArrowRight className="w-3 h-3 ml-1.5" />
            </Button>
            {onSaveToJournal && treeForNav?.id && treeName && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSaveToJournal(lead)}
                disabled={isSavingToJournal}
                className="h-7 px-2 text-[10px] border-border text-muted-foreground hover:text-foreground"
                data-testid={`save-lead-to-journal-${lead.id}`}
              >
                {isSavingToJournal ? <Loader2 className="w-3 h-3 animate-spin" /> : <BookmarkPlus className="w-3 h-3 mr-1" />}
                Add to Journal
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

// Main Investigation Leads Panel
const InvestigationLeadsPanel = ({ treeId, treeSlug, treeName }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOpen, setIsOpen] = useState(true);
  const [savingToJournalId, setSavingToJournalId] = useState(null);

  const handleSaveToJournal = async (lead) => {
    if (!treeId || !treeName) return;
    setSavingToJournalId(lead.id);
    try {
      await axios.post(`${API}/investigations`, {
        tree_id: treeId,
        tree_name: treeName,
        insight_id: lead.id,
        insight_type: 'lead',
        insight_title: lead.title,
        insight_description: lead.description,
        search_params: lead.action_params || {},
        linked_individual_ids: (lead.related_people || []).map((p) => p.id).filter(Boolean),
        notes: null
      });
      toast.success('Lead added to Research Journal');
    } catch (err) {
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already saved')) {
        toast.info('This lead is already in your Research Journal');
      } else {
        toast.error(err.response?.data?.detail || 'Could not add to journal');
      }
    } finally {
      setSavingToJournalId(null);
    }
  };
  
  useEffect(() => {
    const fetchLeads = async () => {
      if (!treeId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response = await axios.get(`${API}/trees/${treeId}/leads`);
        setLeads(response.data.leads || []);
      } catch (err) {
        console.error('Failed to fetch investigation leads:', err);
        setError('Unable to generate leads');
      } finally {
        setLoading(false);
      }
    };
    
    fetchLeads();
  }, [treeId]);
  
  const handleInvestigate = (lead) => {
    toast.success(`Opening investigation: ${lead.title}`, {
      duration: 2000
    });
  };
  
  // Don't render if no leads and not loading
  if (!loading && leads.length === 0) {
    return null;
  }
  
  return (
    <div className="w-full" data-testid="investigation-leads-panel">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <Card className="bg-gradient-to-br from-card to-stone-950/80 border-secondary shadow-lg">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-secondary transition-colors py-3 px-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-amber-900/30 border border-amber-800/40">
                    <Compass className="w-4 h-4 text-amber-500" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground/90">
                      Investigation Leads
                    </h3>
                    <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                      Automatically generated research suggestions
                    </p>
                  </div>
                  {leads.length > 0 && (
                    <span className="ml-2 px-2 py-0.5 text-[10px] font-medium rounded-full bg-amber-900/30 text-amber-400 border border-amber-800/40">
                      {leads.length}
                    </span>
                  )}
                </div>
                {isOpen ? (
                  <ChevronUp className="w-4 h-4 text-muted-foreground/60" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-muted-foreground/60" />
                )}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          
          <CollapsibleContent>
            <CardContent className="pt-0 px-4 pb-4">
              {loading ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-5 h-5 text-amber-500/60 animate-spin" />
                  <span className="ml-2 text-xs text-muted-foreground">Analyzing your tree...</span>
                </div>
              ) : error ? (
                <div className="text-center py-4">
                  <p className="text-xs text-muted-foreground/70">{error}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <AnimatePresence mode="popLayout">
                    {leads.map((lead, index) => (
                      <motion.div
                        key={lead.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ delay: index * 0.1 }}
                      >
                        <LeadCard 
                          lead={lead} 
                          treeForNav={{ id: treeId, slug: treeSlug }}
                          treeName={treeName}
                          onInvestigate={handleInvestigate}
                          onSaveToJournal={handleSaveToJournal}
                          isSavingToJournal={savingToJournalId === lead.id}
                        />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  
                  {/* Footer note */}
                  <p className="text-[10px] text-muted-foreground/50 text-center pt-2 italic">
                    Leads update as your tree grows
                  </p>
                </div>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};

export default InvestigationLeadsPanel;
